import axios from 'axios';
import sessionStorage from '../utils/sessionStorage';

/** Production browser must never use localhost — Vercel/local env sometimes sets REACT_APP_API_URL to dev. */
function resolveApiBaseUrl() {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV !== 'production') {
    return fromEnv || 'http://localhost:5001/api';
  }
  if (!fromEnv || /localhost|127\.0\.0\.1/.test(fromEnv)) {
    return '/api';
  }
  return fromEnv.replace(/\/$/, '');
}

const API_URL = resolveApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: process.env.NODE_ENV === 'production' ? 30000 : 10000, // Longer timeout for production
  headers: {
    'Content-Type': 'application/json',
  },
});

// Retry configuration for failed requests
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

// Helper function to check if error should be retried
const shouldRetry = (error) => {
  // Don't retry on authentication errors
  if (error.response?.status === 401) {
    return false;
  }
  
  // Retry on network errors, timeouts, and server errors
  return (
    !error.response || // Network error
    error.code === 'ECONNABORTED' || // Timeout
    error.response.status >= 500 || // Server error
    error.response.status === 429 // Rate limit
  );
};

// Helper function to delay execution
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = sessionStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with conservative authentication handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const config = error.config;
    
    // Initialize retry count if not present
    if (!config.__retryCount) {
      config.__retryCount = 0;
    }
    
    // Check if we should retry this request (only for non-auth errors)
    if (config.__retryCount < MAX_RETRIES && shouldRetry(error)) {
      config.__retryCount += 1;
      
      console.log(`Retrying request (${config.__retryCount}/${MAX_RETRIES}): ${config.url}`);
      
      // Wait before retrying
      await delay(RETRY_DELAY * config.__retryCount);
      
      // Retry the request
      return api(config);
    }
    
    // Handle 401 errors very conservatively
    if (error.response?.status === 401) {
      const isTokenVerification = error.config?.url?.includes('/auth/verify');
      
      if (!isTokenVerification) {
        // Only logout if we're absolutely sure it's an auth failure
        // Be very conservative to prevent false logouts
        const hasValidToken = sessionStorage.getItem('authToken');
        const isRecentLogin = Date.now() - (sessionStorage.getItem('loginTime') || 0) < 30000; // 30 seconds
        
        if (hasValidToken && !isRecentLogin) {
          console.error('Unauthorized access - redirecting to login');
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('user');
          window.location.href = '/login';
        } else {
          console.log('Recent login or no token - not logging out, letting AuthContext handle');
        }
      } else {
        console.log('Token verification failed - will be handled by AuthContext');
      }
    } else if (error.response?.status === 500) {
      console.error('Server error');
    } else if (error.code === 'ECONNABORTED') {
      console.error('Request timeout');
    }
    
    return Promise.reject(error);
  }
);

// Authentication API functions
export const authAPI = {
  // User registration (Step 1: Send OTP)
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  // Verify OTP and complete registration (Step 2)
  verifyOTPAndRegister: async (userData) => {
    const response = await api.post('/auth/verify-otp', userData);
    return response.data;
  },

  // Send OTP with delivery method
  sendOTP: async (email, phoneNumber, deliveryMethod) => {
    const response = await api.post('/auth/send-otp', {
      email,
      phoneNumber,
      deliveryMethod
    });
    return response.data;
  },

  // Resend OTP
  resendOTP: async (email, phoneNumber = null, deliveryMethod = 'email') => {
    const response = await api.post('/auth/resend-otp', {
      email,
      phoneNumber,
      deliveryMethod
    });
    return response.data;
  },

  // User login
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  // Google login
  googleLogin: async (credential) => {
    const response = await api.post('/auth/google', { credential });
    return response.data;
  },

  // Get current user profile
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  // Update user profile
  updateProfile: async (profileData) => {
    const response = await api.put('/auth/profile', profileData);
    return response.data;
  },

  // Change password
  changePassword: async (passwordData) => {
    const response = await api.put('/auth/change-password', passwordData);
    return response.data;
  },

  // Logout
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  // Verify token
  verifyToken: async () => {
    const response = await api.get('/auth/verify');
    return response.data;
  },

  // Forgot password - Step 1: Request password reset
  forgotPasswordRequest: async (identifier) => {
    const response = await api.post('/auth/forgot-password', { identifier });
    return response.data;
  },

  // Forgot password - Step 2: Send password reset OTP
  sendPasswordResetOTP: async (email, phoneNumber, deliveryMethod) => {
    const response = await api.post('/auth/send-password-reset-otp', {
      email,
      phoneNumber,
      deliveryMethod
    });
    return response.data;
  },

  // Forgot password - Step 3: Reset password with OTP
  resetPassword: async (email, otp, newPassword) => {
    const response = await api.post('/auth/reset-password', {
      email,
      otp,
      newPassword
    });
    return response.data;
  }
};

// User Booking API functions
export const bookingAPI = {
  // Create a new booking request
  createBookingRequest: async (bookingData) => {
    const response = await api.post('/bookings/create', bookingData);
    return response.data;
  },

  // Get user's booking requests
  getUserBookings: async (params = {}) => {
    const response = await api.get('/bookings', { params });
    return response.data;
  },

  // Get user's booking statistics
  getUserBookingStats: async () => {
    const response = await api.get('/bookings/stats');
    return response.data;
  },

  // Get a single booking request by ID
  getUserBooking: async (id) => {
    const response = await api.get(`/bookings/${id}`);
    return response.data;
  },

  // Download booking invoice PDF
  downloadBookingInvoice: async (bookingId) => {
    const response = await api.get(`/bookings/${bookingId}/pdf/download`, {
      responseType: 'blob'
    });
    return response;
  },

  // View booking invoice PDF
  viewBookingInvoice: async (bookingId) => {
    const response = await api.get(`/bookings/${bookingId}/pdf/view`, {
      responseType: 'blob'
    });
    return response;
  }
};

// Payment API functions
export const paymentAPI = {
  // Create a new payment request
  createPaymentRequest: async (paymentData) => {
    const response = await api.post('/payments/create', paymentData);
    return response.data;
  },

  // Get all payment requests with optional filters
  getPaymentRequests: async (params = {}) => {
    const response = await api.get('/payments', { params });
    return response.data;
  },

  // Get payment statistics
  getPaymentStats: async () => {
    const response = await api.get('/payments/stats');
    return response.data;
  },

  // Get a single payment request by ID
  getPaymentRequest: async (id) => {
    const response = await api.get(`/payments/${id}`);
    return response.data;
  },

  // Get a single payment request by ID (public endpoint for PDF generation)
  getPaymentRequestPublic: async (id) => {
    const response = await api.get(`/payments/public/${id}`);
    return response.data;
  },

  // Get payment request by Stripe session ID
  getPaymentRequestBySession: async (sessionId) => {
    const response = await api.get(`/payments/session/${sessionId}`);
    return response.data;
  },

  // Get payment request by Stripe session ID (public access for success page)
  getPaymentRequestBySessionPublic: async (sessionId) => {
    const response = await api.get(`/payments/public/session/${sessionId}`);
    return response.data;
  },

  // Get flight locations for dropdowns
  getFlightLocations: async () => {
    const response = await api.get('/payments/flight-locations');
    return response.data;
  },

  // Download payment invoice PDF
  downloadPaymentInvoice: async (paymentId) => {
    const response = await api.get(`/payments/${paymentId}/pdf/download`, {
      responseType: 'blob'
    });
    return response;
  },

  // View payment invoice PDF
  viewPaymentInvoice: async (paymentId) => {
    const response = await api.get(`/payments/${paymentId}/pdf/view`, {
      responseType: 'blob'
    });
    return response;
  },

  // Send booking invoice via email
  sendBookingInvoice: async (paymentId) => {
    const response = await api.post(`/payments/${paymentId}/pdf/send`);
    return response.data;
  },

  // Send payment confirmation with invoice via email
  sendPaymentConfirmation: async (paymentId) => {
    const response = await api.post(`/payments/${paymentId}/payment/send`);
    return response.data;
  },

  // Send booking confirmation email with frontend-generated PDF
  sendBookingConfirmationEmail: async (bookingData, recipientEmail, serviceType = 'arrival', paymentRequestId = null) => {
    const response = await api.post('email/send-frontend-pdf', {
      bookingData,
      serviceType,
      recipientEmail,
      emailType: 'booking_confirmation',
      paymentRequestId
    });
    return response.data;
  },

  // Send payment confirmation email with frontend-generated PDF
  sendPaymentConfirmationEmail: async (paymentData, recipientEmail, paymentRequestId = null) => {
    const response = await api.post('email/send-frontend-pdf', {
      bookingData: paymentData,
      serviceType: 'payment',
      recipientEmail,
      emailType: 'payment_confirmation',
      paymentRequestId
    });
    return response.data;
  },

  // Resend payment request
  resendPayment: async (paymentId) => {
    const response = await api.post(`/payments/${paymentId}/resend`);
    return response.data;
  },

  // Delete payment request
  deletePayment: async (paymentId) => {
    const response = await api.delete(`/payments/${paymentId}/delete`);
    return response.data;
  },

  // Send payment link via email
  sendPaymentLink: async (paymentId) => {
    const response = await api.post(`/payments/${paymentId}/send-link`);
    return response.data;
  },

  // Send WhatsApp payment confirmation
  sendWhatsAppPaymentConfirmation: async (phoneNumber, paymentData) => {
    const response = await api.post('/whatsapp/send-payment-confirmation-message', {
      phoneNumber,
      paymentData
    });
    return response.data;
  },

  // Send WhatsApp booking confirmation
  sendWhatsAppBookingConfirmation: async (phoneNumber, bookingData) => {
    const response = await api.post('/whatsapp/send-booking-confirmation-message', {
      phoneNumber,
      bookingData
    });
    return response.data;
  },

  // Send WhatsApp greeter confirmation
  sendWhatsAppGreeterConfirmation: async (phoneNumber, greeterData) => {
    const response = await api.post('/whatsapp/send-greeter-confirmation-message', {
      phoneNumber,
      greeterData
    });
    return response.data;
  },

  // Get assigned bookings
  getAssignedBookings: async () => {
    const response = await api.get('/assigned-bookings');
    return response.data;
  },

  // Get greeters
  getGreeters: async () => {
    const response = await api.get('/greeters');
    return response.data;
  },
};

// Service API functions
export const serviceAPI = {
  // Get all service categories
  getAllCategories: async () => {
    const response = await api.get('/services/categories');
    return response.data;
  },

  // Get a specific service category by ID
  getCategoryById: async (categoryId) => {
    const response = await api.get(`/services/categories/${categoryId}`);
    return response.data;
  },

  // Get service levels for a specific category
  getServiceLevels: async (categoryId) => {
    const response = await api.get(`/services/categories/${categoryId}/levels`);
    return response.data;
  },

  // Get a specific service level
  getServiceLevel: async (categoryId, levelId) => {
    const response = await api.get(`/services/categories/${categoryId}/levels/${levelId}`);
    return response.data;
  },

  // Calculate pricing for a service
  calculatePricing: async (categoryId, levelId, additionalServices = {}) => {
    const response = await api.post(`/services/categories/${categoryId}/levels/${levelId}/pricing`, {
      additionalServices
    });
    return response.data;
  },

  // Get all service levels across all categories (for comparison)
  getAllServiceLevels: async () => {
    const response = await api.get('/services/levels');
    return response.data;
  },

  // Get pricing comparison across all levels for a category
  getPricingComparison: async (categoryId, additionalServices = {}) => {
    const params = additionalServices ? { additionalServices: JSON.stringify(additionalServices) } : {};
    const response = await api.get(`/services/categories/${categoryId}/comparison`, { params });
    return response.data;
  },

  // Get service features and benefits
  getServiceFeatures: async () => {
    const response = await api.get('/services/features');
    return response.data;
  },

  // Dynamic Pricing API functions
  // Get pricing status and cache info
  getPricingStatus: async () => {
    const response = await api.get('/services/pricing/status');
    return response.data;
  },

  // Force refresh pricing from website
  refreshPricingFromWebsite: async () => {
    const response = await api.post('/services/pricing/refresh');
    return response.data;
  },

  // Get all service categories with dynamic pricing
  getAllCategoriesWithDynamicPricing: async () => {
    const response = await api.get('/services/categories/dynamic');
    return response.data;
  },

  // Calculate pricing with dynamic data
  calculateDynamicPricing: async (categoryId, levelId, additionalServices = {}) => {
    const response = await api.post(`/services/categories/${categoryId}/levels/${levelId}/pricing/dynamic`, {
      additionalServices
    });
    return response.data;
  },

  // Start automatic pricing updates
  startAutoUpdates: async (intervalMinutes = 30) => {
    const response = await api.post('/services/pricing/auto-update', {
      intervalMinutes
    });
    return response.data;
  },

  // Compare static vs dynamic pricing
  comparePricing: async () => {
    const response = await api.get('/services/pricing/compare');
    return response.data;
  },
};

// Greeter API functions
export const greeterAPI = {
  // Create a new greeter
  createGreeter: async (greeterData) => {
    const response = await api.post('/greeters', greeterData);
    return response.data;
  },

  // Get all greeters with booking counts (optimized)
  getAllGreetersWithBookings: async () => {
    const response = await api.get('/greeters/with-bookings');
    return response.data;
  },

  // Get all greeters
  getAllGreeters: async () => {
    const response = await api.get('/greeters');
    return response.data;
  },

  // Get available greeters
  getAvailableGreeters: async () => {
    const response = await api.get('/greeters/available');
    return response.data;
  },

  // Get greeter by ID
  getGreeterById: async (id) => {
    const response = await api.get(`/greeters/${id}`);
    return response.data;
  },

  // Update greeter
  updateGreeter: async (id, updateData) => {
    const response = await api.put(`/greeters/${id}`, updateData);
    return response.data;
  },

  // Delete greeter
  deleteGreeter: async (id) => {
    const response = await api.delete(`/greeters/${id}`);
    return response.data;
  },

  // Update greeter availability (greeter endpoint)
  updateAvailability: async (id, availabilityData) => {
    const response = await api.put(`/greeters/${id}/my-availability`, availabilityData);
    return response.data;
  },

  // Update greeter availability (admin endpoint)
  updateGreeterAvailability: async (id, availabilityData) => {
    const response = await api.put(`/greeters/${id}/availability`, availabilityData);
    return response.data;
  },

  // Assign booking to greeter
  assignBooking: async (assignmentData) => {
    const response = await api.post('/greeters/assign-booking', assignmentData);
    return response.data;
  },

  // Get greeter's assigned bookings
  getGreeterBookings: async (id) => {
    const response = await api.get(`/greeters/${id}/bookings`);
    return response.data;
  },

    // Get greeter info for a specific booking (user access)
    getGreeterInfoForBooking: async (bookingId) => {
      const response = await api.get(`/greeters/booking/${bookingId}/info`);
      return response.data;
    },

    // Get greeter dashboard data
  getGreeterDashboard: async (id) => {
    const response = await api.get(`/greeters/${id}/dashboard`);
    return response.data;
  },

  // Get greeter's own bookings (for greeter role)
  getMyBookings: async (id) => {
    const response = await api.get(`/greeters/${id}/my-bookings`);
    return response.data;
  }
};

// Assigned Booking API functions
export const assignedBookingAPI = {
  // Create assigned booking from payment request
  createAssignedBooking: async (data) => {
    const response = await api.post('/assigned-bookings/create', data);
    return response.data;
  },

  // Get assigned bookings for a specific greeter
  getGreeterAssignedBookings: async (greeterId) => {
    const response = await api.get(`/assigned-bookings/greeter/${greeterId}`);
    return response.data;
  },

  // Mark booking as completed
  markBookingCompleted: async (bookingId, notes = '') => {
    const response = await api.patch(`/assigned-bookings/${bookingId}/complete`, { notes });
    return response.data;
  },

  // Mark booking as unavailable
  markBookingUnavailable: async (bookingId, notes = '', reason = '') => {
    const response = await api.patch(`/assigned-bookings/${bookingId}/unavailable`, { notes, reason });
    return response.data;
  },

  // Update unavailable reason
  updateUnavailableReason: async (bookingId, reason) => {
    const response = await api.patch(`/assigned-bookings/${bookingId}/unavailable-reason`, { reason });
    return response.data;
  },

  // Get unavailable bookings for admin
  getUnavailableBookings: async () => {
    const response = await api.get('/assigned-bookings/unavailable');
    return response.data;
  },

  // Reassign unavailable booking to another greeter
  reassignBooking: async (bookingId, newGreeterId, notes = '') => {
    const response = await api.patch(`/assigned-bookings/${bookingId}/reassign`, { 
      newGreeterId, 
      notes 
    });
    return response.data;
  },

  // Get all assigned bookings for admin
  getAllAssignedBookings: async (filters = {}) => {
    const response = await api.get('/assigned-bookings', { params: filters });
    return response.data;
  }
};

// Webhook API functions
export const webhookAPI = {
  // Test webhook endpoint
  testWebhook: async () => {
    const response = await api.get('/webhooks/test');
    return response.data;
  },
};

// Finance Operator API functions
export const financeOperatorAPI = {
  // Create a new finance operator
  createFinanceOperator: async (financeOperatorData) => {
    const response = await api.post('/finance-operators', financeOperatorData);
    return response.data;
  },

  // Get all finance operators
  getAllFinanceOperators: async () => {
    const response = await api.get('/finance-operators');
    return response.data;
  },

  // Get finance operator by ID
  getFinanceOperatorById: async (id) => {
    const response = await api.get(`/finance-operators/${id}`);
    return response.data;
  },

  // Update finance operator
  updateFinanceOperator: async (id, updateData) => {
    const response = await api.put(`/finance-operators/${id}`, updateData);
    return response.data;
  },

  // Delete/deactivate finance operator
  deleteFinanceOperator: async (id) => {
    const response = await api.delete(`/finance-operators/${id}`);
    return response.data;
  },

  // Get pending approval payments
  getPendingApprovalPayments: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    
    const response = await api.get(`/finance-operators/payments/pending-approval?${params.toString()}`);
    return response.data;
  },

  // Approve payment
  approvePayment: async (paymentId) => {
    const response = await api.post(`/finance-operators/payments/${paymentId}/approve`);
    return response.data;
  }
};

// Health check
export const healthCheck = async () => {
  const response = await api.get('/health');
  return response.data;
};

export default api;
